import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  listPulseMatches,
  patchPulseMatch,
} from '../../services/pulseApi';
import {
  MATCH_STATUS_LABELS,
  competitionLabel,
  matchBlockedByCompetition,
  matchFlagOn,
  matchStatusChipColor,
  parseMatchesPayload,
} from './pulseUtils';

function PulseMatches() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseMatches();
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse matches.'));
        setMatches([]);
        return;
      }
      setMatches(parseMatchesPayload(getPulsePayload(result)));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse matches.');
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const handleToggle = async (match, field, value) => {
    const key = `${match.match_id}:${field}`;
    const previous =
      field === 'fanwars_enabled' || field === 'ratings_enabled'
        ? matchFlagOn(match[field])
        : !!match[field];
    setToggling(key);
    setMatches((prev) =>
      prev.map((m) => (m.match_id === match.match_id ? { ...m, [field]: value } : m))
    );

    try {
      const result = await patchPulseMatch(match.match_id, { [field]: value });
      if (!isPulseApiSuccess(result)) {
        setMatches((prev) =>
          prev.map((m) => (m.match_id === match.match_id ? { ...m, [field]: previous } : m))
        );
        setError(getPulseErrorMessage(result, 'Failed to update match Pulse flags.'));
        return;
      }
      const payload = getPulsePayload(result);
      const updated = payload?.match || payload;
      if (updated && typeof updated === 'object' && updated.match_id != null) {
        setMatches((prev) =>
          prev.map((m) => (m.match_id === match.match_id ? { ...m, ...updated } : m))
        );
      }
      setSnackbar({
        open: true,
        message: 'Saved. Cron picks this up on the next 10 min tick.',
      });
    } catch (toggleError) {
      setMatches((prev) =>
        prev.map((m) => (m.match_id === match.match_id ? { ...m, [field]: previous } : m))
      );
      setError(toggleError.message || 'Failed to update match Pulse flags.');
    } finally {
      setToggling('');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return matches;
    return matches.filter((m) => {
      const hay = [
        m.title,
        m.teama,
        m.teamb,
        m.match_id,
        m.status_str,
        m.competition_title,
        m.competition_abbr,
        m.competition_cid,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [matches, search]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Match enable</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button component={Link} to="/admin/pulse/competitions" size="small">
            Competitions
          </Button>
          <IconButton onClick={loadMatches} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Fixtures in a ~2-day-past / 7-day-ahead window. <strong>Pulse</strong> is the master switch
        (off → no Instinct / Knowledge / Fan Wars / Ratings for that fixture).
        <strong> Instinct</strong> = pre-match PRED-01..06 (24h → start).
        <strong> Knowledge</strong> = post-match who-won + TRIVIA-01..05 (end → +24h).
        <strong> Fan Wars</strong> = pre-match FW-01 (24h → start, default on).
        <strong> Ratings</strong> = post-match Match Rating / Rate Player (end → +3h, default on;
        independent of Knowledge). Match and competition gates must both allow generation — if
        competition Pulse is off, generators skip the fixture even when match Pulse is on. Existing
        cards stay; expire them from Cards to take them off the app deck. Needs migration{' '}
        <code>216_pulse_fanwars_ratings_toggles.sql</code>.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Match, team, competition, id"
          InputProps={{ endAdornment: <SearchIcon fontSize="small" color="action" /> }}
          sx={{ minWidth: 280 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Match</TableCell>
                <TableCell>Competition</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Status</TableCell>
                <TableCell title="Master switch">Pulse</TableCell>
                <TableCell title="Pre-match PRED-01..06 (24h → start)">Instinct</TableCell>
                <TableCell title="Post-match who-won + TRIVIA-01..05 (end → +24h)">Knowledge</TableCell>
                <TableCell title="Pre-match Pulse Fan Wars FW-01 (24h → start). Default on">
                  Fan Wars
                </TableCell>
                <TableCell title="Post-match Match Rating / Rate Player (end → +3h). Default on">
                  Ratings
                </TableCell>
                <TableCell align="right">Cards</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No matches in the Pulse window.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((match) => {
                  const statusLabel =
                    match.status_str || MATCH_STATUS_LABELS[match.status] || match.status;
                  const blocked = matchBlockedByCompetition(match);
                  const compName = competitionLabel(match);
                  return (
                    <TableRow key={match.match_id} hover sx={{ opacity: blocked ? 0.75 : 1 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {match.title || `${match.teama || '—'} vs ${match.teamb || '—'}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{match.match_id}
                          {match.teama || match.teamb
                            ? ` · ${match.teama || '?'} vs ${match.teamb || '?'}`
                            : ''}
                        </Typography>
                        {blocked && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip size="small" color="warning" label={blocked} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {compName ? (
                          <>
                            <Typography variant="body2">{compName}</Typography>
                            {match.competition_cid != null && (
                              <Typography variant="caption" color="text.secondary">
                                CID {match.competition_cid}
                              </Typography>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{match.date_start ? formatDate(match.date_start) : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={statusLabel} color={matchStatusChipColor(match.status)} />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={!!match.pulse_enabled}
                          disabled={toggling === `${match.match_id}:pulse_enabled`}
                          onChange={(e) => handleToggle(match, 'pulse_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={!!match.instinct_enabled}
                          disabled={toggling === `${match.match_id}:instinct_enabled`}
                          onChange={(e) => handleToggle(match, 'instinct_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={!!match.knowledge_enabled}
                          disabled={toggling === `${match.match_id}:knowledge_enabled`}
                          onChange={(e) => handleToggle(match, 'knowledge_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={matchFlagOn(match.fanwars_enabled)}
                          disabled={toggling === `${match.match_id}:fanwars_enabled`}
                          onChange={(e) => handleToggle(match, 'fanwars_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={matchFlagOn(match.ratings_enabled)}
                          disabled={toggling === `${match.match_id}:ratings_enabled`}
                          onChange={(e) => handleToggle(match, 'ratings_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          component={Link}
                          to={`/admin/pulse?match_id=${match.match_id}`}
                          size="small"
                        >
                          View cards
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseMatches;
